// =============================================================================
// Painel White Label — Gestão de Tenants
// Layout padronizado com a listagem "Todos os Negócios" (CrmDeals):
//   - Tipografia compacta (TableHead text-xs / TableCell text-xs py-2)
//   - Badges substituídos por bolinha colorida + texto preto (status/plano)
//   - Novas colunas: Telefone do owner (LGPD reveal), Agentes, Inboxes
//   - Coluna "Limites" reformulada (Agentes/Canais em duas linhas)
//   - Coluna "Criado em" com hora (dd/MM/yyyy HH:mm)
//   - Filtro por período (CrmPeriodFilter)
//   - Busca também por email/telefone do owner
// @version 3.1.0
// RELEASE: 2026-05-29 v3.1.0 - Removida edicao manual (botao lapis + Dialog).
//                              Tenants sao alterados apenas por contratacoes.
// RELEASE: 2026-05-28 v3.0.0 - Refator visual + novas colunas/filtros.
// RELEASE: 2026-05-28 v2.0.0 - Migrado para shell ListPageLayout/KpiCard.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Building2,
  Search,
  ListChecks,
  CalendarPlus,
  CheckCircle2,
  PauseCircle,
  Clock,
  XCircle,
  RefreshCw,
  Eye,
  EyeOff,
  Users,
  Inbox,
  Gift,
  X as XIcon,
} from "lucide-react";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from "@/components/layout/list-page";
import { PaginationFooter } from "@/components/ui/pagination-footer";
import { useWlTenantKpis } from "@/hooks/useWlListKpis";
import { useWlRevealTenantPhone } from "@/hooks/useWlRevealTenantPhone";
import { useWlPlans } from "@/hooks/useWlPlans";
import { CrmPeriodFilter } from "@/components/crm/filters/CrmPeriodFilter";
import { calculatePeriodDates, type PeriodFilter, type PeriodPreset } from "@/hooks/useCrmOverviewFilters";
import { cn } from "@/lib/utils";
import { WlTenantDetailsModal } from "@/components/wl/WlTenantDetailsModal";

interface WlTenantRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  max_agents: number;
  max_channels: number;
  created_at: string;
  trial_ends_at: string | null;
  suspension_reason: string | null;
  owner_email: string | null;
  owner_name: string | null;
  owner_phone_masked: string | null;
  members_count: number;
  agents_count: number;
  inboxes_count: number;
  is_courtesy: boolean;
  /** v1.5.0 da RPC: bloco de trial */
  trial_started_at: string | null;
  trial_ends_at_effective: string | null;
  trial_days_used: number | null;
  total_count: number;
}

const STATUS_OPTIONS = ["active", "suspended", "trial", "cancelled"] as const;
// Plan options agora vêm dinamicamente via useWlPlans (inclui inativos para admin/WL)
const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

// Mapas de cores (bolinha) — texto sempre em foreground
const STATUS_DOT: Record<string, string> = {
  active: "bg-green-500",
  suspended: "bg-red-500",
  trial: "bg-yellow-500",
  cancelled: "bg-zinc-400",
};

const STATUS_LABEL: Record<string, string> = {
  active: "Ativo",
  suspended: "Suspenso",
  trial: "Trial",
  cancelled: "Cancelado",
};

const PLAN_DOT: Record<string, string> = {
  free: "bg-zinc-400",
  starter: "bg-blue-500",
  professional: "bg-violet-500",
  enterprise: "bg-amber-500",
};

/** Renderiza bolinha colorida + texto (substitui Badge balão). */
function DotLabel({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-foreground whitespace-nowrap">
      <span className={cn("h-2 w-2 rounded-full shrink-0", color)} />
      {label}
    </span>
  );
}

/**
 * v3.2.0 (2026-08-18) — Célula "Trial".
 * Calcula os dias corridos restantes do período de teste no fuso de São Paulo,
 * comparando apenas a data (sem hora) para evitar arredondamentos estranhos.
 * Só exibe informação quando o tenant está com status 'trial'.
 */
function TrialCell({ status, trialEndsAt }: { status: string; trialEndsAt: string | null }) {
  if (status !== "trial" || !trialEndsAt) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  // Converte "agora" e o fim do trial para a data-calendário de São Paulo
  const spDate = (d: Date) =>
    new Date(d.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const end = startOfDay(spDate(new Date(trialEndsAt)));
  const today = startOfDay(spDate(new Date()));
  const diffDays = Math.round((end.getTime() - today.getTime()) / 86_400_000);

  const label =
    diffDays > 0
      ? `${diffDays} ${diffDays === 1 ? "dia" : "dias"}`
      : diffDays === 0
        ? "Vence hoje"
        : `Vencido há ${Math.abs(diffDays)} ${Math.abs(diffDays) === 1 ? "dia" : "dias"}`;

  const tone =
    diffDays >= 3
      ? "text-emerald-600"
      : diffDays >= 0
        ? "text-amber-600"
        : "text-destructive";

  return (
    <div className="text-xs leading-tight whitespace-nowrap">
      <div className={cn("font-medium", tone)}>{label}</div>
      <div className="text-[11px] text-muted-foreground">
        {format(new Date(trialEndsAt), "dd/MM/yyyy")}
      </div>
    </div>
  );
}

/**
 * v3.3.0 (2026-08-18) — Célula "Trial usado".
 * Exibe o total de dias de período de teste já usufruídos pelo tenant.
 * O valor vem calculado da RPC `wl_list_tenants` (v1.5.0), no fuso de São Paulo:
 *  - tenants ainda em trial: dias corridos desde a criação até hoje;
 *  - tenants que já saíram do trial: total de dias do período concedido (valor congelado).
 */
function TrialUsedCell({
  daysUsed,
  startedAt,
}: {
  daysUsed: number | null;
  startedAt: string | null;
}) {
  if (daysUsed === null || daysUsed === undefined) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <div className="text-xs leading-tight whitespace-nowrap">
      <div className="font-medium text-foreground">
        {daysUsed} {daysUsed === 1 ? "dia" : "dias"}
      </div>
      {startedAt && (
        <div className="text-[11px] text-muted-foreground">
          desde {format(new Date(startedAt), "dd/MM/yyyy")}
        </div>
      )}
    </div>
  );
}





export default function WlTenants() {
  const { toast } = useToast();

  const [rows, setRows] = useState<WlTenantRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("__all");
  const [planFilter, setPlanFilter] = useState<string>("__all");
  // Filtro Cortesia: "__all" (default), "only" (somente cortesia), "none" (sem cortesia)
  const [courtesyFilter, setCourtesyFilter] = useState<"__all" | "only" | "none">("__all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  // KPI ativo (toggle de filtro por status)
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);

  // Modal de detalhes do tenant (RPCs wl_get_tenant_*)
  const [detailsTenantId, setDetailsTenantId] = useState<string | null>(null);

  // Filtro por período (default: TODOS — não envia recorte de data para a RPC)
  // Mantém um PeriodFilter válido para o componente CrmPeriodFilter (que exige datas),
  // mas só envia `created_from/created_to` à RPC quando o usuário ativa um preset.
  const [period, setPeriod] = useState<PeriodFilter>(() => {
    const { startDate, endDate } = calculatePeriodDates("month");
    return { preset: "month", startDate, endDate };
  });
  const [periodActive, setPeriodActive] = useState(false);

  // Reveal LGPD do telefone
  const { revealed, reveal, hide, isRevealing } = useWlRevealTenantPhone();


  // Lista dinâmica de planos (inclui inativos para permitir busca por planos legados)
  const { data: wlPlans = [] } = useWlPlans({ includeInactive: true });
  const planOptions = useMemo(
    () => wlPlans.slice().sort((a, b) => a.sort_order - b.sort_order).map((p) => p.name),
    [wlPlans],
  );

  const effectiveStatus = useMemo(() => {
    if (kpiFilter && kpiFilter !== "created_today" && kpiFilter !== "courtesy") return kpiFilter;
    return statusFilter === "__all" ? null : statusFilter;
  }, [statusFilter, kpiFilter]);

  // Só envia datas para a RPC quando o usuário ativa um período (evita ocultar tenants antigos).
  const periodIso = useMemo(
    () => ({
      from: periodActive ? period.startDate.toISOString() : null,
      to: periodActive ? period.endDate.toISOString() : null,
    }),
    [period, periodActive],
  );

  // Traduz courtesyFilter em booleano/null para as RPCs.
  const courtesyValue = useMemo<boolean | null>(() => {
    if (kpiFilter === "courtesy") return true;
    if (courtesyFilter === "only") return true;
    if (courtesyFilter === "none") return false;
    return null;
  }, [courtesyFilter, kpiFilter]);

  const kpiFilters = useMemo(() => {
    const base = {
      search: search || undefined,
      plan: planFilter === "__all" ? null : planFilter,
      status: statusFilter === "__all" ? null : statusFilter,
      created_from: periodIso.from,
      created_to: periodIso.to,
      is_courtesy: courtesyValue,
    };
    if (kpiFilter && kpiFilter !== "created_today" && kpiFilter !== "courtesy") base.status = null;
    // KPI Cortesia ativo não deve restringir status
    if (kpiFilter === "courtesy") base.status = null;
    return base;
  }, [search, statusFilter, planFilter, kpiFilter, periodIso, courtesyValue]);



  const { data: kpis, isLoading: kpisLoading } = useWlTenantKpis(kpiFilters);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("wl_list_tenants", {
        p_search: search || null,
        p_status: effectiveStatus,
        p_plan: planFilter === "__all" ? null : planFilter,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
        p_created_from: periodIso.from,
        p_created_to: periodIso.to,
        p_is_courtesy: courtesyValue,
      } as never);
      if (error) throw error;
      const list = (data || []) as unknown as WlTenantRow[];
      setRows(list);
      setTotal(list.length ? Number(list[0].total_count) : 0);
    } catch (e: any) {
      toast({ title: "Erro ao carregar tenants", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter, planFilter, kpiFilter, search, periodIso.from, periodIso.to, courtesyFilter]);

  const toggleKpi = (key: string | null) => {
    setKpiFilter((prev) => (prev === key ? null : key));
    setPage(1);
  };

  /** Handlers do CrmPeriodFilter — qualquer interação ativa o recorte de período. */
  const handlePresetChange = (preset: PeriodPreset) => {
    if (preset === "custom") return;
    const { startDate, endDate } = calculatePeriodDates(preset);
    setPeriod({ preset, startDate, endDate });
    setPeriodActive(true);
    setPage(1);
  };
  const handleCustomChange = (startDate: Date, endDate: Date) => {
    setPeriod({ preset: "custom", startDate, endDate });
    setPeriodActive(true);
    setPage(1);
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Tenants"
        description="Gestão de organizações do white label"
        actions={
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        }
      />

      <ListPageKpis columns={7}>
        <KpiCard color="purple" icon={ListChecks} label="Total" value={kpisLoading ? "…" : kpis?.total ?? 0}
          onClick={() => toggleKpi(null)} active={kpiFilter === null} />
        <KpiCard color="cyan" icon={CalendarPlus} label="Criados Hoje" value={kpisLoading ? "…" : kpis?.created_today ?? 0}
          onClick={() => toggleKpi("created_today")} active={kpiFilter === "created_today"} />
        <KpiCard color="green" icon={CheckCircle2} label="Ativos" value={kpisLoading ? "…" : kpis?.active ?? 0}
          onClick={() => toggleKpi("active")} active={kpiFilter === "active"} />
        <KpiCard color="red" icon={PauseCircle} label="Suspensos" value={kpisLoading ? "…" : kpis?.suspended ?? 0}
          onClick={() => toggleKpi("suspended")} active={kpiFilter === "suspended"} />
        <KpiCard color="yellow" icon={Clock} label="Trial" value={kpisLoading ? "…" : kpis?.trial ?? 0}
          onClick={() => toggleKpi("trial")} active={kpiFilter === "trial"} />
        <KpiCard color="orange" icon={XCircle} label="Cancelados" value={kpisLoading ? "…" : kpis?.cancelled ?? 0}
          onClick={() => toggleKpi("cancelled")} active={kpiFilter === "cancelled"} />
        {/* KPI Cortesia — clique alterna filtro "Somente cortesia". */}
        <KpiCard color="blue" icon={Gift} label="Cortesia" value={kpisLoading ? "…" : kpis?.courtesy ?? 0}
          onClick={() => toggleKpi("courtesy")} active={kpiFilter === "courtesy"} />
      </ListPageKpis>

      <ListPageFiltersBar isFetching={loading}>
        <div className="relative flex-1 min-w-[220px] sm:flex-none sm:w-[280px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7 sm:pl-8")}
            placeholder="Nome, slug, email ou telefone…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearch(searchInput);
                setPage(1);
              }
            }}
            onBlur={() => {
              if (searchInput !== search) {
                setSearch(searchInput);
                setPage(1);
              }
            }}
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setKpiFilter(null); setPage(1); }}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[140px]")}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos status</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s] || s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(1); }}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[140px]")}>
            <SelectValue placeholder="Plano" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos planos</SelectItem>
            {planOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Filtro Cortesia — default "Todos" (retorna cortesia + não-cortesia). */}
        <Select
          value={courtesyFilter}
          onValueChange={(v) => {
            setCourtesyFilter(v as "__all" | "only" | "none");
            // Se usuário mexe manualmente, desativa KPI "Cortesia" para evitar conflito.
            if (kpiFilter === "courtesy") setKpiFilter(null);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[150px]")}>
            <SelectValue placeholder="Cortesia" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos (cortesia)</SelectItem>
            <SelectItem value="only">Somente cortesia</SelectItem>
            <SelectItem value="none">Sem cortesia</SelectItem>
          </SelectContent>
        </Select>
        <CrmPeriodFilter
          value={period}
          onPresetChange={handlePresetChange}
          onCustomChange={handleCustomChange}
          className="h-6 text-[11px] sm:h-8 sm:text-sm"
        />
        {/* Botão "Todos": remove recorte de período (default da tela). */}
        {periodActive && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[11px] sm:h-8 sm:text-sm gap-1"
            onClick={() => { setPeriodActive(false); setPage(1); }}
            title="Remover filtro de período (Todos)"
          >
            <XIcon className="h-3 w-3" />
            Todos
          </Button>
        )}
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        {loading ? (
          <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Building2 className="h-10 w-10 opacity-40" />
            Nenhum tenant encontrado
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium">Nome</TableHead>
                <TableHead className="text-xs font-medium">Slug</TableHead>
                <TableHead className="text-xs font-medium">Owner</TableHead>
                <TableHead className="text-xs font-medium">Telefone</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
                {/* v3.2.0 — dias restantes do período de teste */}
                <TableHead className="text-xs font-medium">Trial</TableHead>
                {/* v3.3.0 — total de dias de trial já usufruídos */}
                <TableHead className="text-xs font-medium">Trial usado</TableHead>


                <TableHead className="text-xs font-medium">Plano</TableHead>
                <TableHead className="text-xs font-medium text-center">
                  <span className="inline-flex items-center gap-1">
                    <Users className="h-3 w-3" /> Agentes
                  </span>
                </TableHead>
                <TableHead className="text-xs font-medium text-center">
                  <span className="inline-flex items-center gap-1">
                    <Inbox className="h-3 w-3" /> Inboxes
                  </span>
                </TableHead>
                <TableHead className="text-xs font-medium">Limites</TableHead>
                <TableHead className="text-xs font-medium">Criado em</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => {
                const revealedPhone = revealed[t.id];
                const hasPhone = !!t.owner_phone_masked;
                return (
                  <TableRow
                    key={t.id}
                    onClick={() => setDetailsTenantId(t.id)}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell className="py-2 text-xs font-medium">{t.name}</TableCell>
                    <TableCell className="py-2 text-xs text-muted-foreground">{t.slug}</TableCell>
                    <TableCell className="py-2 text-xs">
                      <div className="text-foreground">{t.owner_name || t.owner_email || "—"}</div>
                      {t.owner_email && t.owner_name && (
                        <div className="text-[11px] text-muted-foreground">{t.owner_email}</div>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-xs">
                      {hasPhone ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span className="font-mono text-[11px] text-foreground">
                            {revealedPhone ?? t.owner_phone_masked}
                          </span>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-5 w-5"
                            disabled={isRevealing(t.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              revealedPhone ? hide(t.id) : reveal(t.id);
                            }}
                            title={revealedPhone ? "Ocultar telefone" : "Revelar telefone (auditado)"}
                          >
                            {revealedPhone ? (
                              <EyeOff className="h-3 w-3" />
                            ) : (
                              <Eye className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <DotLabel
                        color={STATUS_DOT[t.status] || "bg-zinc-400"}
                        label={STATUS_LABEL[t.status] || t.status}
                      />
                    </TableCell>
                    <TableCell className="py-2">
                      {/* Usa o fim efetivo (considera prorrogações do binding) */}
                      <TrialCell
                        status={t.status}
                        trialEndsAt={t.trial_ends_at_effective ?? t.trial_ends_at}
                      />
                    </TableCell>
                    <TableCell className="py-2">
                      <TrialUsedCell
                        daysUsed={t.trial_days_used}
                        startedAt={t.trial_started_at ?? t.created_at}
                      />
                    </TableCell>


                    <TableCell className="py-2">
                      <DotLabel
                        color={PLAN_DOT[t.plan] || "bg-zinc-400"}
                        label={t.plan}
                      />
                    </TableCell>
                    <TableCell className="py-2 text-xs text-center">{t.agents_count}</TableCell>
                    <TableCell className="py-2 text-xs text-center">{t.inboxes_count}</TableCell>
                    <TableCell className="py-2">
                      <div className="text-[11px] leading-tight">
                        <div>
                          <span className="text-muted-foreground">Agentes:</span>{" "}
                          <span className="text-foreground">{t.max_agents}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Canais:&nbsp;</span>
                          <span className="text-foreground">{t.max_channels}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(t.created_at), "dd/MM/yyyy HH:mm")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </ListPageFooter>

      {/* Modal de detalhes do tenant — abre ao clicar em uma linha da listagem */}
      <WlTenantDetailsModal
        tenantId={detailsTenantId}
        open={!!detailsTenantId}
        onClose={() => setDetailsTenantId(null)}
      />
    </ListPageLayout>
  );
}
